import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-monokai';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import { getPartnerGuid, hasAuthourity } from '../../lib/access.es6';
import {
  getTaxConfigsRequest,
  getTaxConfigRawRequest,
  getTaxConfigRawReset,
  publishTaxConfigRequest,
  publishTaxConfigReset,
  createTaxConfigRequest,
  createTaxConfigReset,
} from './actions.es6';

var TH = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};
var TD = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

function newTaxConfigTemplate(partner) {
  return [
    `partner: ${partner}`,
    `type: kra_pin`,
    `enabled: true`,
  ].join('\n');
}

class TaxConfigs extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editorOpen: false,
      isNew: false,
      selectedConfig: null,
      editorContent: '',
      hasYamlErrors: false,
    };
  }

  componentWillMount() {
    this.props.dispatch(getTaxConfigsRequest(getPartnerGuid()));
  }

  componentDidUpdate(prevProps) {
    const { tax_config_raw, successfully_published, successfully_created } = this.props;

    if (tax_config_raw && tax_config_raw !== prevProps.tax_config_raw) {
      this.setState({ editorContent: tax_config_raw });
    }
    if (successfully_published && successfully_published !== prevProps.successfully_published) {
      this.closeEditor();
    }
    if (successfully_created && successfully_created !== prevProps.successfully_created) {
      this.closeEditor();
    }
  }

  openEdit(config) {
    this.props.dispatch(getTaxConfigRawReset());
    this.props.dispatch(publishTaxConfigReset());
    this.setState({ editorOpen: true, isNew: false, selectedConfig: config, editorContent: '' });
    this.props.dispatch(getTaxConfigRawRequest(config.partner, config.type));
  }

  openCreate() {
    this.props.dispatch(getTaxConfigRawReset());
    this.props.dispatch(createTaxConfigReset());
    this.setState({
      editorOpen: true,
      isNew: true,
      selectedConfig: null,
      editorContent: newTaxConfigTemplate(getPartnerGuid()),
    });
  }

  closeEditor() {
    this.setState({ editorOpen: false, selectedConfig: null, editorContent: '' });
    this.props.dispatch(getTaxConfigRawReset());
    this.props.dispatch(publishTaxConfigReset());
    this.props.dispatch(createTaxConfigReset());
  }

  handlePublish() {
    const { editorContent, isNew, selectedConfig } = this.state;
    const partner = getPartnerGuid();
    if (isNew) {
      this.props.dispatch(createTaxConfigRequest(partner, editorContent));
    } else {
      this.props.dispatch(publishTaxConfigRequest(partner, selectedConfig.type, editorContent));
    }
  }

  render() {
    if (!hasAuthourity('VIEW_UNDERWRITING_FLOWS')) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to view KYC Tax Configs.
          </Typography>
        </Box>
      );
    }

    const {
      loader, tax_configs, get_configs_error,
      config_raw_loader, get_config_raw_error,
      publish_loader, publish_error,
      create_loader, create_error,
    } = this.props;
    const { editorOpen, isNew, selectedConfig, editorContent, hasYamlErrors } = this.state;

    const saving = publish_loader || create_loader;
    const saveError = publish_error || create_error;

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUserOutlinedIcon sx={{ color: '#6B7280' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
              KYC Tax Configs
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.openCreate()}
            sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' }, textTransform: 'none' }}
          >
            New Config
          </Button>
        </Box>

        {get_configs_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load tax configs. Please try again.
          </Alert>
        )}

        {loader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>Partner</TableCell>
                  <TableCell sx={TH}>Type</TableCell>
                  <TableCell sx={TH}>Enabled</TableCell>
                  <TableCell sx={TH} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!tax_configs || tax_configs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ ...TD, textAlign: 'center', color: '#9CA3AF', py: 4 }}>
                      No tax configs found for this partner.
                    </TableCell>
                  </TableRow>
                ) : (
                  tax_configs.map(config => (
                    <TableRow key={`${config.partner}:${config.type}`} hover>
                      <TableCell sx={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>{config.partner}</TableCell>
                      <TableCell sx={{ ...TD, fontWeight: 500 }}>
                        <Chip label={config.type} size="small" sx={{ fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={TD}>
                        <Chip
                          label={config.enabled !== false ? 'enabled' : 'disabled'}
                          size="small"
                          color={config.enabled !== false ? 'success' : 'default'}
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell sx={TD} align="right">
                        <Tooltip title="Edit YAML">
                          <IconButton size="small" onClick={() => this.openEdit(config)} sx={{ color: '#0D47A1' }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={editorOpen}
          onClose={() => this.closeEditor()}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: '85vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isNew ? 'New Tax Config' : (selectedConfig && `${selectedConfig.partner} — ${selectedConfig.type}`)}
              </Typography>
              {!isNew && selectedConfig && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6B7280' }}>
                  {selectedConfig.partner}:{selectedConfig.type}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => this.closeEditor()}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
            {saveError && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                {(saveError && saveError.message) || 'Failed to save config. Check your YAML is valid.'}
              </Alert>
            )}
            {get_config_raw_error && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                Failed to load config content.
              </Alert>
            )}

            {config_raw_loader ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <AceEditor
                  mode="yaml"
                  theme="monokai"
                  value={editorContent}
                  onChange={value => this.setState({ editorContent: value })}
                  onValidate={annotations => this.setState({ hasYamlErrors: annotations.some(a => a.type === 'error') })}
                  name="tax-config-yaml-editor"
                  width="100%"
                  height="100%"
                  fontSize={13}
                  showPrintMargin={false}
                  showGutter={true}
                  highlightActiveLine={true}
                  editorProps={{ $blockScrolling: true }}
                  setOptions={{
                    enableBasicAutocompletion: true,
                    enableLiveAutocompletion: false,
                    showLineNumbers: true,
                    tabSize: 2,
                    useWorker: true,
                  }}
                />
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #E5E7EB' }}>
            <Button onClick={() => this.closeEditor()} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
              onClick={() => this.handlePublish()}
              disabled={saving || config_raw_loader || !editorContent || hasYamlErrors}
              sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' }, textTransform: 'none' }}
            >
              {saving ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    loader: state.taxConfigs.loader,
    tax_configs: state.taxConfigs.tax_configs,
    get_configs_error: state.taxConfigs.get_configs_error,

    config_raw_loader: state.taxConfigs.config_raw_loader,
    tax_config_raw: state.taxConfigs.tax_config_raw,
    get_config_raw_error: state.taxConfigs.get_config_raw_error,

    publish_loader: state.taxConfigs.publish_loader,
    successfully_published: state.taxConfigs.successfully_published,
    publish_error: state.taxConfigs.publish_error,

    create_loader: state.taxConfigs.create_loader,
    successfully_created: state.taxConfigs.successfully_created,
    create_error: state.taxConfigs.create_error,
  };
}

export default connect(mapStateToProps)(TaxConfigs);
